import { getMemoizedLangFunction } from "Utils";
import {
  SHOW_FILTER_TYPES,
  FILTER_TYPE_TO_VARIABLE_NAME,
  DEFAULT_FILTERS,
  COURSE_STANDARD_STATUS_OPTIONS,
  SHOW_FILTER_OPTIONS,
  FILTERS_TO_SHOW,
  FEEDLIST_COLUMN_IDS,
  FEEDLIST_COLUMN_LIST,
  FEEDLIST_PROPERTIES_BY_COLUMN_ID,
  FILTERS_TO_DATA_GRID_COLUMN_ID,
} from "./stringConstants";
import { FILTER_TYPES } from "PlannerElementDataSet/modules/PlannerElementDataSetConstants";
import _ from "lodash";

export const getTransformedData = ({ standardEdges, t, value }) => {
  const columnnProperties = FEEDLIST_PROPERTIES_BY_COLUMN_ID(t);
  const currentColumns = FEEDLIST_COLUMN_LIST;

  const columnList = {};
  _.forEach(currentColumns, columnId => {
    columnList[columnId] = columnnProperties[columnId];
  });

  const feedData = _.chain(standardEdges)
    .map(edge => {
      const { standardSet = {}, standards } = edge || {};
      const { id = "", title = "", isArchived = false, subject } = standardSet;

      const standardIds = _.map(standards, "id");
      const totalCount = _.size(standardIds);
      const selectedCount = _.intersection(standardIds, value || []).length;

      const { isArchived: isSubjectArchived = false } = subject || {};

      const data = {
        id,
        isArchived: isArchived || isSubjectArchived,
      };

      _.forEach(currentColumns, columnId => {
        switch (columnId) {
          case FEEDLIST_COLUMN_IDS.NAME:
            data[columnId] = title;
            break;
          case FEEDLIST_COLUMN_IDS.STANDARDS_SELECTED:
            data[columnId] = `${selectedCount}/${totalCount}`;
            break;
          default:
            data[columnId] = "";
        }
      });

      return data;
    })
    .value();

  return { feedData, columnList };
};

export const getCurrentFiltersFromFilterKeys = ({ filters }) => {
  const currentFilters = {};

  _.forEach(filters, (filterValue, filterKey) => {
    switch (filterKey) {
      case FILTER_TYPES.SHOW: {
        const varName = FILTER_TYPE_TO_VARIABLE_NAME[FILTER_TYPES.SHOW];
        if (filterValue === SHOW_FILTER_TYPES.POWER_STANDARD) {
          currentFilters[varName] = true;
        }
        break;
      }
      default: {
        currentFilters[FILTER_TYPE_TO_VARIABLE_NAME[filterKey]] = filterValue;
        break;
      }
    }
  });

  return currentFilters;
};

export const getAllFiltersCount = ({ currentFilters }) => {
  const defaultFilters = DEFAULT_FILTERS;

  return _.reduce(
    currentFilters,
    (acc, filterValue, filterKey) => {
      switch (filterKey) {
        case FILTER_TYPES.SEARCH:
          return acc;
        default: {
          if (_.isEqual(filterValue, defaultFilters[filterKey])) {
            return acc;
          } else {
            return acc + 1;
          }
        }
      }
    },
    0
  );
};

export const getAppliedFiltersCountForFilter = ({ filterKey, filterValue }) => {
  const defaultFilters = DEFAULT_FILTERS;
  const defaultFilterValue = defaultFilters[filterKey];

  if (_.isEqual(filterValue, defaultFilterValue)) {
    return 0;
  }

  return _.isArray(filterValue) ? _.size(filterValue) : 1;
};

const getLocalizedFilterLabels = getMemoizedLangFunction(({ t }) => ({
  [FILTER_TYPES.GRADES]: t("scopeAndSequence:grade_levels"),
  [FILTER_TYPES.SUBJECT_AREAS]: t("scopeAndSequence:subject_areas"),
  [FILTER_TYPES.TAGS]: t("common:tags"),
  [FILTER_TYPES.STATUS]: t("common:status"),
  [FILTER_TYPES.SHOW]: t("scopeAndSequence:power_standards"),
}));

const getStandardDetailsFilterOptions = getMemoizedLangFunction(({ t }) =>
  COURSE_STANDARD_STATUS_OPTIONS(t)
);

export const getShowFilterOptions = getMemoizedLangFunction(({ t }) =>
  SHOW_FILTER_OPTIONS(t)
);

export const getFilterOverlayOptions = ({
  grades,
  subjects,
  tags,
  defaultColumns,
}) => {
  const localizedFilterLabels = getLocalizedFilterLabels();

  return _.chain(FILTERS_TO_SHOW)
    .filter(filter => {
      const correspondingColumn = FILTERS_TO_DATA_GRID_COLUMN_ID[filter];

      if (correspondingColumn) {
        return _.includes(defaultColumns, correspondingColumn);
      }

      return true;
    })
    .map(filter => {
      switch (filter) {
        case FILTER_TYPES.GRADES: {
          return {
            id: FILTER_TYPES.GRADES,
            label: localizedFilterLabels[FILTER_TYPES.GRADES],
            optionsType: "checkbox",
            filterOptions: _.map(grades, ({ id, name }) => ({
              id,
              label: name,
            })),
            includeAllOption: true,
            showSearch: true,
          };
        }

        case FILTER_TYPES.SUBJECT_AREAS: {
          return {
            id: FILTER_TYPES.SUBJECT_AREAS,
            label: localizedFilterLabels[FILTER_TYPES.SUBJECT_AREAS],
            optionsType: "checkbox",
            filterOptions: _.map(subjects, ({ id, name }) => ({
              id,
              label: name,
            })),
            includeAllOption: true,
            showSearch: true,
          };
        }

        case FILTER_TYPES.TAGS: {
          return {
            id: FILTER_TYPES.TAGS,
            label: localizedFilterLabels[FILTER_TYPES.TAGS],
            optionsType: "checkbox",
            filterOptions: tags,
            includeAllOption: true,
            showSearch: true,
          };
        }

        case FILTER_TYPES.STATUS: {
          return {
            id: FILTER_TYPES.STATUS,
            label: localizedFilterLabels[FILTER_TYPES.STATUS],
            optionsType: "radio",
            filterOptions: getStandardDetailsFilterOptions(),
          };
        }

        case FILTER_TYPES.SHOW: {
          return {
            id: FILTER_TYPES.SHOW,
            label: localizedFilterLabels[FILTER_TYPES.SHOW],
            optionsType: "radio",
            filterOptions: getShowFilterOptions(),
          };
        }
      }
    })
    .value();
};

export const getApplyScopeMap = getMemoizedLangFunction(({ t }) => ({
  SINGLE_STUDENT: t("transcript:apply_for_this_student"),
  MULTIPLE_STUDENTS: t("progressReport:apply_for_multiple_label", {
    label: t("common:student_plural_lowercase"),
  }),
  MULTIPLE_CLASSES: t("progressReport:apply_for_multiple_label", {
    label: t("common:classes"),
  }),
}));

export const getApplyOptions = getMemoizedLangFunction(({ t }) =>
  _.map(getApplyScopeMap({ t }), (label, key) => ({ key, label }))
);

export const getFirstSetWithSelectedStandards = params => {
  const { value, standardEdges } = params || {};
  if (_.isEmpty(value)) return null;

  return _.find(standardEdges, edge => {
    const { standards = [] } = edge;
    const standardIds = _.map(standards, "id");

    return !_.isEmpty(_.intersection(value, standardIds));
  })?.standardSet?.id;
};
